"use client";

import { useState, useCallback, useRef } from "react";

export type ConfirmCallback<T = unknown> = (value?: T) => void | Promise<void>;

const NOOP_CONFIRM_CALLBACK = () => undefined;

export type ConfirmationDialogController<T = unknown> = {
  isOpen: boolean;
  onOpen: (confirmCallback: ConfirmCallback<T>) => void;
  onClose: () => void;
  onCloseWithCleanup: (cleanupCallback?: () => void) => void;
  onConfirm: (value?: T) => Promise<void>;
};

export default function useConfirmationDialog<
  T = unknown,
>(): ConfirmationDialogController<T> {
  const [isOpen, setIsOpen] = useState(false);
  const callbackRef = useRef<ConfirmCallback<T>>(NOOP_CONFIRM_CALLBACK);

  const closeDialog = useCallback(() => {
    callbackRef.current = NOOP_CONFIRM_CALLBACK;
    setIsOpen(false);
  }, []);

  const onOpen = useCallback((confirmCallback: ConfirmCallback<T>) => {
    callbackRef.current = confirmCallback;
    setIsOpen(true);
  }, []);

  const onClose = useCallback(() => {
    closeDialog();
  }, [closeDialog]);

  const onCloseWithCleanup = useCallback(
    (cleanupCallback?: () => void) => {
      cleanupCallback?.();
      closeDialog();
    },
    [closeDialog],
  );

  const onConfirm = useCallback(
    async (value?: T) => {
      await callbackRef.current(value);
      closeDialog();
    },
    [closeDialog],
  );

  return {
    isOpen,
    onOpen,
    onClose,
    onCloseWithCleanup,
    onConfirm,
  };
}
